import { FC } from "react"

import { useTranslation } from "react-i18next"

import { CmsPage } from "services/cms-seo/type"

import { inputClass, LockBox, SectionHeading, WarnBox } from "../CmsUi"

interface CustomCodePaneProps {
  page: CmsPage
  onChange: (patch: Partial<CmsPage>) => void
  /** Brand-level admins see the tab read-only; only platform admins may write it. */
  canEdit: boolean
}

export const CustomCodePane: FC<CustomCodePaneProps> = ({ page, onChange, canEdit }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  return (
    <div>
      <LockBox>
        <b>{t("cmsSeo.platformAdminOnly", { defaultValue: "Platform admin only." })}</b>{" "}
        {t("cmsSeo.customCodeLock", {
          defaultValue:
            "Brand-level admins see this tab read-only. Every save is written to the audit log with a diff, and scripts are served with a CSP nonce."
        })}
      </LockBox>

      <WarnBox>
        {t("cmsSeo.customCodeWarn", {
          defaultValue:
            "Code here applies to this page only, runs after site-wide custom code, and goes live when the page is published. Broken markup here can break the page for every visitor."
        })}
      </WarnBox>

      <SectionHeading>{t("cmsSeo.headCode", { defaultValue: "Inside <head> tag" })}</SectionHeading>
      <textarea
        className={`${inputClass} min-h-[150px] font-mono text-xs`}
        value={page.head_code ?? ""}
        disabled={!canEdit}
        spellCheck={false}
        placeholder="<!-- e.g. affiliate pixel, verification meta -->"
        onChange={(event) => onChange({ head_code: event.target.value || null })}
      />

      <SectionHeading>{t("cmsSeo.bodyCode", { defaultValue: "Before </body> tag" })}</SectionHeading>
      <textarea
        className={`${inputClass} min-h-[150px] font-mono text-xs`}
        value={page.body_code ?? ""}
        disabled={!canEdit}
        spellCheck={false}
        onChange={(event) => onChange({ body_code: event.target.value || null })}
      />
    </div>
  )
}
